import { useState, useEffect, type SyntheticEvent } from 'react';
import { useNavigate, Navigate, Link, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { MdLock, MdPerson, MdDirectionsBike } from 'react-icons/md';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSuccess] = useState(() => !!(location.state as { resetSuccess?: boolean })?.resetSuccess);

  useEffect(() => {
    if (resetSuccess) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/perfil" replace />;
  }

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      setIsLoading(true);
      await login({ email, password });
      navigate('/perfil');
    } catch {
      setError('E-mail ou senha incorretos');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-600 to-brand-800 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-100 rounded-full mb-4">
              <MdDirectionsBike className="text-3xl text-brand-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">RandoBR</h1>
            <p className="text-gray-600">Portal do Randonneur</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {resetSuccess && (
              <div className="bg-brand-50 border border-brand-200 text-brand-700 px-4 py-3 rounded-lg text-sm">
                Senha redefinida com sucesso. Faça login com a nova senha.
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdPerson className="text-gray-400 text-xl" />
                </div>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-10" placeholder="Digite seu e-mail"
                  disabled={isLoading} autoComplete="email" required />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdLock className="text-gray-400 text-xl" />
                </div>
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10" placeholder="Digite sua senha"
                  disabled={isLoading} autoComplete="current-password" required />
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2">
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Entrando...</span>
                </>
              ) : (
                <span>Entrar</span>
              )}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center gap-3">
            <Link to="/esqueci-minha-senha" className="text-sm text-brand-600 hover:text-brand-700 font-medium">
              Esqueceu sua senha?
            </Link>
            <p className="text-sm text-gray-500">
              Não tem conta?{' '}
              <Link to="/cadastro" className="text-brand-600 hover:text-brand-700 font-medium">
                Criar conta
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-white text-sm mt-8">&copy; 2026 RandoBR. Todos os direitos reservados.</p>
      </div>
    </div>
  );
}
